import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, MapPin, MessageCircle, Star } from "lucide-react";
import { PageShell } from "../components/PageShell";
import { Reveal, MaskLines } from "../components/Reveal";
import { useLang, SERVICES, WHATSAPP, ADDRESS, MAPS, IMG } from "../lib/i18n";
import { slugForService } from "../lib/treatments";
import { GLOW_PATH } from "../lib/season";
import { useSeo } from "../lib/seo";

const COPY = {
  it: {
    kicker: "Catanzaro · Via Tre Fontane 37",
    h1: "Centro estetico a Catanzaro",
    lead: "Gioia Centro Estetico è il tuo punto di riferimento a Catanzaro per trattamenti viso, corpo, epilazione laser, mani e piedi. Ogni percorso parte da una consulenza personalizzata, con metodo e prodotti professionali.",
    book: "Prenota online", wa: "Scrivici su WhatsApp", maps: "Apri in Google Maps",
    rating: "4,8 su Google",
    treatments: "I trattamenti a Catanzaro",
    discover: "Scopri",
    whyT: "Perché scegliere Gioia",
    why: [
      ["Consulenza su misura", "Ogni trattamento inizia ascoltando te: obiettivi, pelle e abitudini."],
      ["4,8 su Google", "Le clienti di Catanzaro raccontano la loro esperienza nelle recensioni."],
      ["Gioia Skincare", "Una linea di creme per continuare la cura a casa, dopo la cabina."],
    ],
    more: "Non sai da dove iniziare?",
    quiz: "Fai il quiz della pelle", gift: "Idea regalo: Pacchetto Glow", blog: "Leggi il Journal",
    seoT: "Centro estetico a Catanzaro · Gioia Centro Estetico",
    seoD: "Centro estetico a Catanzaro in Via Tre Fontane 37: lipolaser, mesoterapia, mesoporazione, trattamenti viso pro-age, depilazione laser, manicure. 4,8 su Google.",
  },
  en: {
    kicker: "Catanzaro · Via Tre Fontane 37",
    h1: "Beauty centre in Catanzaro",
    lead: "Gioia Centro Estetico is your beauty destination in Catanzaro for face and body treatments, laser hair removal, hands and feet. Every programme starts with a personal consultation, with method and professional products.",
    book: "Book online", wa: "Message us on WhatsApp", maps: "Open in Google Maps",
    rating: "4.8 on Google",
    treatments: "Treatments in Catanzaro",
    discover: "Discover",
    whyT: "Why choose Gioia",
    why: [
      ["Tailored consultation", "Every treatment starts by listening to you: goals, skin and habits."],
      ["4.8 on Google", "Clients in Catanzaro share their experience in their reviews."],
      ["Gioia Skincare", "A line of creams to continue your care at home, after the salon."],
    ],
    more: "Not sure where to start?",
    quiz: "Take the skin quiz", gift: "Gift idea: Glow Package", blog: "Read the Journal",
    seoT: "Beauty centre in Catanzaro · Gioia Centro Estetico",
    seoD: "Beauty centre in Catanzaro, Via Tre Fontane 37: lipolaser, mesotherapy, mesoporation, pro-age facials, laser hair removal, manicure. 4.8 on Google.",
  },
};

const btn = "inline-flex justify-center items-center gap-2 rounded-full px-7 py-4 text-[0.72rem] tracking-[0.2em] uppercase transition-colors duration-500";

const Hero = ({ c }) => (
  <div className="grid lg:grid-cols-12 gap-12 items-center">
    <div className="lg:col-span-7">
      <p className="overline text-[#8A6A3A] flex items-center gap-2"><MapPin className="w-4 h-4" strokeWidth={1.5} /> {c.kicker}</p>
      <h1 className="mt-6 font-serif-d font-light text-[3rem] sm:text-7xl lg:text-8xl leading-[0.92] text-[#14110F]" data-testid="hub-title">
        <MaskLines lines={[c.h1]} delay={0.1} />
      </h1>
      <p className="mt-6 inline-flex items-center gap-2 text-sm text-[#14110F]" data-testid="hub-rating"><Star className="w-4 h-4 fill-[#C9A66B] text-[#C9A66B]" /> {c.rating}</p>
      <p className="mt-6 text-[#4f4841] font-light leading-relaxed max-w-xl text-base sm:text-lg">{c.lead}</p>
      <div className="mt-10 flex flex-col sm:flex-row flex-wrap gap-4">
        <Link to="/#booking" data-testid="hub-book-btn" className={`group ${btn} bg-[#14110F] text-[#F6F2EC] hover:bg-[#8A6A3A]`}>{c.book} <ArrowUpRight className="w-4 h-4 transition-transform duration-500 group-hover:rotate-45" /></Link>
        <a href={WHATSAPP} target="_blank" rel="noreferrer" data-testid="hub-whatsapp-btn" className={`${btn} border border-[#14110F]/25 text-[#14110F] hover:border-[#8A6A3A] hover:text-[#8A6A3A]`}><MessageCircle className="w-4 h-4" /> {c.wa}</a>
      </div>
      <a href={MAPS} target="_blank" rel="noreferrer" data-testid="hub-maps-link" className="mt-8 inline-flex items-center gap-2 text-sm text-[#4f4841] underline underline-offset-4 decoration-[#8A6A3A] hover:text-[#8A6A3A]">
        <MapPin className="w-4 h-4" /> {ADDRESS} · {c.maps}
      </a>
    </div>
    <div className="lg:col-span-5">
      <motion.div initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }} className="relative overflow-hidden aspect-[4/5] rounded-t-[999px] bg-[#14110F]">
        <img src={IMG.cabina} alt="Cabina di Gioia Centro Estetico a Catanzaro" className="absolute inset-0 w-full h-full object-cover" />
      </motion.div>
    </div>
  </div>
);

const Treatments = ({ c, lang }) => (
  <div>
    <Reveal><h2 className="font-serif-d font-light text-4xl sm:text-5xl text-[#14110F] leading-[1]">{c.treatments}</h2></Reveal>
    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#ddd4c7] border border-[#ddd4c7]" data-testid="hub-treatments">
      {SERVICES.map((s, i) => (
        <Reveal key={s.key} delay={i * 0.06} className="bg-[#F6F2EC] p-6 sm:p-8">
          <span className="font-stencil tracking-[0.25em] text-[#8A6A3A]">0{i + 1}</span>
          <h3 className="mt-3 font-serif-d text-3xl font-light text-[#14110F]">{s[lang].t}</h3>
          <ul className="mt-5 space-y-3">
            {s[lang].s.map((name, j) => {
              const slug = slugForService(name);
              return (
                <li key={name} className="text-sm text-[#4f4841]">
                  {slug ? (
                    <Link to={`/trattamenti/${slug}`} data-testid={`hub-link-${s.key}-${j}`} className="group inline-flex items-center gap-1 hover:text-[#8A6A3A] transition-colors duration-300">
                      {name} {lang === "en" ? "in" : "a"} Catanzaro <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:rotate-45" />
                    </Link>
                  ) : name}
                </li>
              );
            })}
          </ul>
        </Reveal>
      ))}
    </div>
  </div>
);

const Why = ({ c }) => (
  <div className="bg-[#14110F] grain text-[#F6F2EC] -mx-5 sm:mx-0 px-6 sm:px-12 lg:px-16 py-16 sm:py-20">
    <Reveal><h2 className="font-serif-d font-light text-4xl sm:text-5xl leading-[1]">{c.whyT}</h2></Reveal>
    <div className="mt-12 grid md:grid-cols-3 gap-10">
      {c.why.map(([t, d], i) => (
        <Reveal key={t} delay={i * 0.08} className="border-t border-[#F6F2EC]/20 pt-6">
          <h3 className="font-serif-d text-2xl sm:text-3xl font-light">{t}</h3>
          <p className="mt-3 text-sm text-[#d9d0c4] font-light leading-relaxed">{d}</p>
        </Reveal>
      ))}
    </div>
  </div>
);

const More = ({ c }) => (
  <div className="border border-[#ddd4c7] p-8 sm:p-14">
    <h2 className="font-serif-d font-light text-4xl sm:text-5xl text-[#14110F] leading-[1]">{c.more}</h2>
    <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-4">
      <Link to="/quiz-pelle" data-testid="hub-quiz-link" className={`${btn} bg-[#C9A66B] text-[#14110F] font-medium hover:bg-[#14110F] hover:text-[#F6F2EC]`}>{c.quiz}</Link>
      <Link to={GLOW_PATH} data-testid="hub-glow-link" className={`${btn} border border-[#14110F]/25 text-[#14110F] hover:border-[#8A6A3A] hover:text-[#8A6A3A]`}>{c.gift}</Link>
      <Link to="/blog" data-testid="hub-blog-link" className={`${btn} border border-[#14110F]/25 text-[#14110F] hover:border-[#8A6A3A] hover:text-[#8A6A3A]`}>{c.blog}</Link>
    </div>
  </div>
);

export default function BeautyCentre() {
  const { lang } = useLang();
  const c = COPY[lang] ?? COPY.it;
  useEffect(() => { window.scrollTo(0, 0); }, []);
  useSeo(c.seoT, c.seoD);
  return (
    <PageShell>
      <div className="max-w-[1280px] mx-auto space-y-24 sm:space-y-32" data-testid="hub-page">
        <Hero c={c} />
        <Treatments c={c} lang={lang} />
        <Why c={c} />
        <More c={c} />
      </div>
    </PageShell>
  );
}
